import { Shimmer, ShimmerElementType, Stack } from "@fluentui/react";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import { useDropdownWithUrlSync } from "../../SharedComponents/Hooks/useDropdownWithUrlSync";
import { useGetPartnerDataQuery } from "../../slices/ExploreCustomers.slice";
import CustomDropdown from "../ProductView/Common/CustomDropdown";
import PartnerChart from "./PartnerChart";
import PartnerTable from "./PartnerTable";
import AIInsightsButtonWithPanel from "../../helper/AIInsightsButtonWithPanel";

const PartnerContainer = (props: any) => {
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("id");

  // Chart filters as array to handle multiple filters
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  // Use the reusable hook for dropdown with URL sync
  const { selectedValue: selectedMetric, handleSelection: handleSelectedMetrics } = useDropdownWithUrlSync({
    validValues: ["All Workloads", "Security Workloads"],
    defaultValue: "Security Workloads",
    onValueChange: useCallback(() => {
      // Clear chart filters when metric changes
      setChartFilters([]);
    }, [])
  });

  const workloadType = selectedMetric === "All Workloads" ? "all" : "securityworkload";
  const { data, error, isFetching } = useGetPartnerDataQuery([tenantId ?? "", workloadType]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [dropdownFilteredData, setDropdownFilteredData] = useState<any>([]);

  const HandleAPIExcpetion = () => (error && <DisplayAPIsException error={error} height={250} />) || "";

  const customProps = {
    tenantId: tenantId,
    tenantName: props?.tenantName
  };

  // Handler to manage array of filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  useEffect(() => {
    logEvent("Partner", "Customer Page", "PartnerContainer", customProps);
  }, []);

  useEffect(() => {
    if (data?.partnerDetailsList) {
      const filteredByMetric = data?.partnerDetailsList.filter(
        (item: any) =>
          item &&
          (selectedMetric === "All Workloads" ||
            (selectedMetric === "Security Workloads" && (item.isSecurityFlag === 1 || item.isSecurityFlag === true)))
      );
      setDropdownFilteredData(filteredByMetric.filter(Boolean));
      setFilteredItems((data?.partnerDetailsList || []).filter(Boolean));
    }

    if (error) {
      logException("Customer Page - Partner Tab", "PartnerContainer", "useGetPartnerDataQuery", error, customProps);
    }
  }, [data, error, selectedMetric]);

  React.useEffect(() => {
    const filteredData = (dropdownFilteredData || []).filter(({ partnerType, partnerOneName }: any) => {
      // If no filters, show all data
      if (chartFilters.length === 0) {
        return true;
      }

      // Apply all filters - item must match ALL active filters
      return chartFilters.every((filter) => {
        if (filter.filterKey === "partnerType") {
          return partnerType === filter.value;
        } else if (filter.filterKey === "partnerOneName") {
          return partnerOneName === filter.value;
        }
        return true;
      });
    });
    setFilteredItems(filteredData);
  }, [chartFilters, dropdownFilteredData]);

  const showNoData = () => {
    const rootStyle = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };

    return selectedMetric === "Security Workloads" ? (
      <div style={rootStyle}>
        {"Security Workloads partner details are not available for " + props.tenantName + "."}
        <br />
        {"To view partner information across all workloads, use View by -> All Workloads."}
      </div>
    ) : (
      <div style={rootStyle}>{"Partner details are not available for " + props.tenantName + "."}</div>
    );
  };

  const displayMetricDropdownWithButton = isFetching ? (
    <div style={{ flexBasis: "100%" }}>
      {" "}
      <Shimmer
        shimmerColors={{ shimmer: "var(--shimmer-color)" }}
        styles={{
          root: { paddingTop: 10, paddingBottom: 10, paddingLeft: 10, width: "25%" },
          shimmerWrapper: { ".ms-ShimmerGap-root": { background: "var(--colorBodyBackground1)" } }
        }}
        shimmerElements={[
          { type: ShimmerElementType.line, width: "15%" },
          { type: ShimmerElementType.gap, width: 10 },
          { type: ShimmerElementType.line, width: "15%" }
        ]}
      />{" "}
    </div>
  ) : (
    <FluentProvider theme={webLightTheme}>
      <Stack verticalAlign="start" horizontal horizontalAlign="space-between" styles={{ root: { marginRight: 8 } }}>
        <CustomDropdown
          defaultOption={{
            optionText: selectedMetric,
            optionValue: selectedMetric,
            selectedOptions: [selectedMetric || ""]
          }}
          onSelectedOption={handleSelectedMetrics}
          data={[
            { value: "Security Workloads", text: "Security Workloads" },
            { value: "All Workloads", text: "All Workloads" }
          ]}
          styles={{ root: { flexBasis: "100%", paddingBottom: 10, paddingLeft: 10 } }}
        />
        <AIInsightsButtonWithPanel
          buttonStyle={{ flex: "1 0 auto" }}
          metricId="7"
          panelTitle="Partner Insights"
          tenantId={tenantId ?? ""}
          tenantName={props.tenantName}
        />
      </Stack>
    </FluentProvider>
  );

  return (
    <>
      {displayMetricDropdownWithButton}
      <PartnerChart
        loading={isFetching}
        onSelectedChartFilter={handleChartFilter}
        options={{
          filteredData: filteredItems,
          originalData: data?.partnerDetailsList || [],
          chartOptions: [
            {
              id: 1,
              field: "partnerType",
              key: "customer-view-customer-partner-chart-by-partnerType",
              title: "Partner Type",
              type: "donut",
              kustoLink: data?.partnerTypeKustoLink
            },
            {
              id: 2,
              field: "partnerOneName",
              key: "customer-view-customer-partner-chart-by-partnerOneName",
              title: "Partner One Name",
              type: "donut",
              kustoLink: data?.partnerOneNameKustoLink
            }
          ]
        }}
      />
      {isFetching ? (
        <CustomShimmer columnCount={11} />
      ) : error ? (
        <HandleAPIExcpetion />
      ) : (
        (Array.isArray(data?.partnerDetailsList) && data?.partnerDetailsList.length && (
          <div style={{ marginLeft: 3, marginRight: 3 }}>
            <PartnerTable
              tableData={filteredItems}
              tenantName={props.tenantName}
              kustoLink={data?.partnerDetailsListKustoLink}
            />
          </div>
        )) ||
        (Array.isArray(data?.partnerDetailsList) && data?.partnerDetailsList.length === 0 && showNoData())
      )}
    </>
  );
};

export default PartnerContainer;
